import { v4 as uuidV4 } from 'uuid'
import { redirect, NOT_FOUND } from 'redux-first-router'

import { customerPartial } from 'ducks/customers/api'
import graphql from 'api/graphql'
import grooveAPI from 'api/groove'
import {
  actionPartials,
  actorPartials,
  basicFieldsWithTwitter,
  filterableFieldsPartials,
} from 'api/tickets'
import metrics from 'util/metrics'

import * as types from 'constants/action_types'
import * as modals from 'constants/modal_types'
import * as pages from 'constants/pages'

import { doMarkFetchingStatus, doUpdateAgentMention } from 'actions/app'
import { doHideModal } from 'actions/modals'
import { doOpenNotePage } from 'actions/pages'
import { doShowSnackbar } from 'actions/snackbar'

import { doFetchTicketSearchByQueryString } from 'actions/search'

import { starPayload } from 'optimistic/star'
import { assignmentPayload, bulkAssignmentPayload } from 'optimistic/assign'
import { mailboxPayload, bulkMailboxPayload } from 'optimistic/mailbox'
import { followPayload } from 'optimistic/follow'
import { selectMailbox } from 'selectors/mailboxes_base'
import { pluralize } from 'util/strings'

import {
  oauthTokenSelector,
  selectCurrentFolderId,
  isAgentInGroup,
  selectGroupsById,
  fetchingStatusesSelector,
  selectDeviceTypeIsMobile,
  selectAccountPrefersScopedContactsSearch,
} from 'selectors/app'
import { selectAgentsById } from 'selectors/agents/base'
import {
  selectCurrentRawChangesetsForTicket,
  selectIsChangesetFetched,
} from 'selectors/currentChangesets'
import { selectCustomersById } from 'ducks/customers/selectors'
import {
  selectCurrentMailboxId,
  selectInaccessibleMailbox,
} from 'selectors/mailboxes'
import { selectAgentMention } from 'selectors/mentions'
import { selectIsAddingNote } from 'selectors/page'
import { selectCurrentRawEmailId } from 'selectors/raw_emails'
import {
  selectCurrentTicketSearchQueryString,
  selectLatestTicketSearchQueryObject,
} from 'selectors/search'
import { selectTicketSorting } from 'selectors/sorting'
import {
  selectTicketsById,
  selectCurrentTicketId,
  selectCurrentTicketIsStarred,
} from 'selectors/tickets'
import { doFetchExternalResourcesForNewActions } from 'ducks/integrations'
import {
  selectDraftIsForwarded,
  selectDraftMailbox,
  doAssignAgentToDraft,
  doAssignGroupToDraft,
  doUpdateDraftMailbox,
} from 'ducks/drafts'
import {
  selectCurrentUser,
  selectAutoAdvanceToNextTicketInList,
} from 'ducks/currentUser/selectors'
import { selectShouldUseSpeedyGroove } from 'ducks/currentUser/selectors/preferences'

import {
  selectCurrentPage,
  currentFolderTicketsSelector,
  selectNextTicketIdInList,
  selectFirstTicketIdInList,
  selectCurrentOrSelectedTickets,
} from 'selectors/ticket_list'
import { selectSelectedTicketIds } from 'selectors/ticket_list/base'
import { selectPreviousUserSearchResults } from 'selectors/users'

import { getLength, all, isEmpty, chunk } from 'util/arrays'
import debug from 'util/debug'
import { runOnNextTick } from 'util/functions'
import GA from 'util/googleAnalytics'
import { batchActions } from 'util/redux'
import { isStarred } from 'util/ticketState'

import editor from 'shared/editor/utils'

import { doSendBulkChangeset, doSendChangeset } from './changeset'

export * from './bulkSelection'
export * from './bulkSelectionMode'
export * from './changeset'
export * from './linkedResources'
export * from './print'
export * from './snooze'
export * from './state'
export * from './actions'

export function doFetchCurrentFolderWithTicketsNextPage() {
  return (dispatch, getState) => {
    const state = getState()
    const folderId = selectCurrentFolderId(state)
    const mailboxId = selectCurrentMailboxId(state)
    const queryString = selectCurrentTicketSearchQueryString(state)
    const page = selectCurrentPage(state) + 1

    metrics.startOrEnhanceTimer(`search-load-${queryString}`, {
      metric: 'search-load',
      logname: 'main',
      hot: false,
      queryString,
      nextPage: true,
    })

    if (queryString) return dispatch(doFetchTicketSearchByQueryString(page))
    if (!folderId) return Promise.resolve(null)

    dispatch({ type: types.CURRENT_FOLDER_WITH_TICKETS_NEXT_PAGE_REQUEST })

    const ticketSorting = selectTicketSorting(state)
    const [sortBy, sortOrder] = ticketSorting.split(':')

    let mailboxIdParam = ''
    if (mailboxId) {
      mailboxIdParam = `mailboxId: "${mailboxId}",`
    }

    const query = `
      query FolderNextPageQuery {
        folder(id: "${folderId}") {
          id
          tickets(${mailboxIdParam} sortBy: "${sortBy}", sortOrder: "${sortOrder}", page: ${page}) {
            metadata {
              currentPage
              totalPages
              totalCount
              sortBy
              sortOrder
            }
            records {
              id
              type
              title
              state
              body
              priority
              message_count
              labels {
                id
                name
              }
              ${filterableFieldsPartials}
              system_updated_at
              snoozedUntil
              mailboxId
              ${customerPartial}
              assignee {
                id
                name
                email
              }
            }
          }
        }
      }
    `
    const token = oauthTokenSelector(state)

    return graphql(token, query)
      .then(res => {
        const responseData = res.json.data
        const data = {
          page,
          folder: responseData.folder,
          mailboxId,
        }
        dispatch({
          type: types.CURRENT_FOLDER_WITH_TICKETS_NEXT_PAGE_SUCCESS,
          data,
        })
        return data
      })
      .catch(err => {
        debug(err)
        dispatch({
          type: types.CURRENT_FOLDER_WITH_TICKETS_NEXT_PAGE_FAIL,
          err,
        })
        return err.json.data // we can still use the partial result data!
      })
      .then(data =>
        dispatch({
          type: types.CURRENT_FOLDER_WITH_TICKETS_NEXT_PAGE_COMPLETE,
          data,
        })
      )
  }
}

export function doFetchCurrentFolderWithTickets() {
  return (dispatch, getState) => {
    const state = getState()
    const folderId = selectCurrentFolderId(state)
    const mailboxId = selectCurrentMailboxId(state)

    if (!folderId) return Promise.resolve(null)

    dispatch({
      type: types.FETCH_FOLDER_WITH_TICKETS_REQUEST,
      data: { folderId, mailboxId },
    })

    const ticketSorting = selectTicketSorting(state)
    const [sortBy, sortOrder] = ticketSorting.split(':')

    let mailboxIdParam = ''
    let mailboxIdParamWithParentheses = '' // eslint-disable-line no-unused-vars
    if (mailboxId) {
      mailboxIdParam = `mailboxId: "${mailboxId}",`
      mailboxIdParamWithParentheses = `(${mailboxIdParam})`
    }

    const query = `
      query FolderQuery {
        folder(id: "${folderId}") {
          id
          name
          tickets(${mailboxIdParam} sortBy: "${sortBy}", sortOrder: "${sortOrder}") {
            metadata {
              sortBy
              sortOrder
              currentPage
              totalPages
              totalCount
            }
            records {
              id
              type
              title
              state
              ${filterableFieldsPartials}
              body
              priority
              message_count
              attachment_count
              labels {
                id
                name
                color
              }
              system_updated_at
              deleted_at
              snoozedUntil
              mailboxId
              ${customerPartial}
              assignee {
                id
                name
                email
                avatar_url
              }
            }
          }
        }
      }
    `
    const token = oauthTokenSelector(state)

    return (
      graphql(token, query)
        .then(res => {
          const data = res.json.data
          dispatch({
            type: types.FETCH_FOLDER_WITH_TICKETS_SUCCESS,
            data: { folder: data.folder, folderId, mailboxId },
          })
        })
        // dont catch errors - let it bubble up as a hard failure
        .then(() =>
          dispatch({ type: types.FETCH_FOLDER_WITH_TICKETS_COMPLETE })
        )
    )
  }
}

function fetchTicketOnMobile(
  dispatch,
  state,
  ticketId,
  { redirectOn404 = true } = {}
) {
  dispatch({ type: types.FETCH_TICKET_REQUEST, data: { ticketId } })
  const token = oauthTokenSelector(state)

  const query = `
      query TicketQuery {
        ticket(id: ${ticketId}) {
          ${basicFieldsWithTwitter}
          drafts {
            body
          }
          actions {
            records {
              created_at
              changeset
              preview
              change_type
              change {
                ${actionPartials}
              }
              actor {
                ${actorPartials}
              }
            }
          }
        }
      }
    `

  return graphql(token, query)
    .then(res => {
      const data = res.json.data
      const { ticket } = data

      dispatch({
        type: types.FETCH_TICKET_SUCCESS,
        data: { ticketId, ticket },
      })
      return ticket
    })
    .catch(err => {
      debug(err)

      dispatch({
        type: types.FETCH_TICKET_FAIL,
        data: { ticketId, err },
      })

      if (err && err.errors && err.errors.some(error => error.status === 404)) {
        dispatch(doFetchTicketMergers(ticketId)).catch(mergerFetchErr => {
          debug(mergerFetchErr)
          if (redirectOn404) dispatch({ type: NOT_FOUND })
          return mergerFetchErr
        })
      }

      throw err
    })
}

const getChangesetParamsIfNeeded = ticket => {
  const hash = window.location.hash
  const hashId = hash ? hash.replace(/^#changeset-/, '') : undefined
  if (!hashId) return null
  const record = ticket.actions.records.find(
    r => `${hashId}-collapsed` === r.changeset
  )
  if (!record) return null
  return {
    ticketId: ticket.id,
    changesetId: record.changeset,
    subchangesetIds: record.change.subchangeset_ids,
  }
}

function fetchTicketOnDesktop(
  dispatch,
  state,
  ticketId,
  { redirectOn404 = true } = {}
) {
  dispatch({ type: types.FETCH_TICKET_REQUEST, data: { ticketId } })
  const token = oauthTokenSelector(state)

  const query = `
      query TicketQuery($ticketId: String, $inlineNotes: Boolean) {
        ticket(id: ${ticketId}) {
          ${basicFieldsWithTwitter}
          drafts {
            body
          }
          conversationDrafts {
            id
            version
            draftType
            payload
          }
        }
        actions(ticketId: $ticketId, inlineNotes: $inlineNotes) {
          records {
            created_at
            changeset
            preview
            change_type
            change {
              ${actionPartials}
            }
            actor {
              ${actorPartials}
            }
          }
        }
      }
    `

  const variables = {
    ticketId,
    inlineNotes: true,
  }
  return graphql(token, query, variables)
    .then(res => {
      const data = res.json.data
      const { ticket, actions } = data
      ticket.actions = actions
      dispatch(doFetchExternalResourcesForNewActions(ticketId, actions.records))
      dispatch({
        type: types.FETCH_TICKET_SUCCESS,
        data: { ticketId, ticket },
      })
      const changesetLoadParams = getChangesetParamsIfNeeded(ticket)
      if (!changesetLoadParams) return ticket
      const { changesetId, subchangesetIds } = changesetLoadParams
      return dispatch(
        doFetchChangesetTicketActions(ticketId, changesetId, subchangesetIds)
      )
    })
    .catch(err => {
      debug(err)

      dispatch({
        type: types.FETCH_TICKET_FAIL,
        data: { ticketId, err },
      })

      if (err && err.errors && err.errors.some(error => error.status === 404)) {
        dispatch(doFetchTicketMergers(ticketId)).catch(mergerFetchErr => {
          debug(mergerFetchErr)
          if (redirectOn404) dispatch({ type: NOT_FOUND })
          return mergerFetchErr
        })
      }

      throw err
    })
}

export function doFetchTicket(ticketId, { redirectOn404 = true } = {}) {
  return (dispatch, getState) => {
    if (!ticketId) return false

    const state = getState()
    const isOnMobile = selectDeviceTypeIsMobile(state)
    const fetchFunc = isOnMobile ? fetchTicketOnMobile : fetchTicketOnDesktop
    return fetchFunc(dispatch, state, ticketId, { redirectOn404 })
  }
}

export function doPreloadNextTicket() {
  return (dispatch, getState) => {
    const state = getState()
    const isViewingTicket = !!selectCurrentTicketId(state)
    let ticketId
    if (isViewingTicket) {
      ticketId = selectNextTicketIdInList(state)
    } else {
      ticketId = selectFirstTicketIdInList(state)
    }

    if (ticketId) return dispatch(doPreloadTicket(ticketId))

    return false
  }
}

export function doPreloadTicket(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const byId = selectTicketsById(state)
    const ticket = byId[ticketId]
    if (!ticket || !ticket.full) {
      const key = `preloadTicket:${ticketId}`
      const isAlreadyFetching = fetchingStatusesSelector(state)[key] === true
      if (!isAlreadyFetching) {
        dispatch(doMarkFetchingStatus(key, true))
        dispatch(doFetchTicket(ticketId))
          .catch(() => debug('Could not prefetch ticket', ticketId))
          .finally(() => {
            dispatch(doMarkFetchingStatus(key, false))
          })
      }
    }
  }
}

export function doFetchTicketMergers(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const query = `
      query TicketMergerQuery {
        mergers(childTicketId: ${ticketId}) {
          records {
            parent_ticket {
              id
            }
          }
        }
      }
    `
    return graphql(token, query).then(res => {
      const records = res.json.data.mergers.records

      if (records.length > 0) {
        return dispatch({
          type: pages.TICKET_PAGE,
          payload: {
            id: records[0].parent_ticket.id,
          },
        })
      }

      throw new Error(`Could not find merger for ticket ${ticketId}`)
    })
  }
}

export function doFetchCurrentTicket() {
  return (dispatch, getState) => {
    const state = getState()
    const ticketId = selectCurrentTicketId(state)
    if (ticketId === 'new') return false
    if (!ticketId) return false

    return dispatch(doFetchTicket(ticketId)).catch(err => {
      debug('doFetchCurrentTicket failed', { err })

      dispatch(doShowSnackbar("Oops, we couldn't fetch that conversation."))
    })
  }
}

export function doFetchChangesetTicketActions(
  ticketId,
  sourceChangesetId,
  changesetIds
) {
  return (dispatch, getState) => {
    if (ticketId === 'new') return false
    if (!ticketId) return false

    const isFetched = selectIsChangesetFetched(getState(), sourceChangesetId)
    if (isFetched) return false

    dispatch({
      type: types.FETCH_CHANGESET_TICKET_ACTIONS_REQUEST,
      data: { ticketId, sourceChangesetId },
    })

    const state = getState()
    const token = oauthTokenSelector(state)

    const query = `
      query TicketActionsQuery($ticketId: String, $changesetIds: [String]) {
        actions(ticketId: $ticketId, changesetIds: $changesetIds) {
          records {
            created_at
            changeset
            preview
            change_type
            change {
              ${actionPartials}
            }
            actor {
              ${actorPartials}
            }
          }
        }
      }
    `

    const variables = {
      ticketId,
      changesetIds,
    }

    GA.track('app', 'load-actions', 'changeset')

    return graphql(token, query, variables)
      .then(res => {
        const data = res.json.data
        metrics.enhanceTimer(`changeset-load-${sourceChangesetId}`, {
          responseRuntime: res.runtime,
          responseLatency: res.latency,
        })
        dispatch({
          type: types.FETCH_CHANGESET_TICKET_ACTIONS_SUCCESS,
          data: { ticketId, sourceChangesetId, actions: data.actions.records },
        })
      })
      .catch(err => {
        debug(err)

        dispatch({
          type: types.FETCH_CHANGESET_TICKET_ACTIONS_FAIL,
          data: { ticketId, sourceChangesetId, err },
        })
      })
  }
}

export function doFetchAllChangesetsTicketActions(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const tickets = selectTicketsById(state)
    const ticket = tickets[ticketId]
    const changesets = selectCurrentRawChangesetsForTicket(state, ticketId)
    const collapsedChangesets = changesets.filter(({ id }) =>
      id.match('collapsed')
    )

    return Promise.all(
      collapsedChangesets.map(changeset => {
        if (isEmpty(changeset.actions)) return null
        const action = changeset.actions[0]
        const change = action.change
        if (!change) return null

        return dispatch(
          doFetchChangesetTicketActions(
            ticket.id,
            changeset.realId,
            change.subchangeset_ids
          )
        )
      })
    )
  }
}

export function doPreFetchChangesetTicketActions(ticketId, force = false) {
  return (dispatch, getState) => {
    const state = getState()
    const shouldPreload = selectShouldUseSpeedyGroove(state)
    if (!force && !shouldPreload) return
    const tickets = selectTicketsById(state)
    const ticket = tickets[ticketId]
    if (!ticket) return
    const changesets = selectCurrentRawChangesetsForTicket(state, ticketId)
    if (!changesets) return

    const filtered = changesets.filter(
      ({ id }) => id.match('collapsed') && !selectIsChangesetFetched(state, id)
    )
    let collapsedChangesets = []
    if (filtered.length > 3) {
      collapsedChangesets.push(filtered[0])
      collapsedChangesets.push(filtered[filtered.length - 2])
      collapsedChangesets.push(filtered[filtered.length - 1])
    } else {
      collapsedChangesets = filtered
    }

    const token = oauthTokenSelector(state)

    const query = `
      query TicketActionsQuery($ticketId: String, $changesetIds: [String]) {
        actions(ticketId: $ticketId, changesetIds: $changesetIds) {
          records {
            created_at
            changeset
            preview
            change_type
            change {
              ${actionPartials}
            }
            actor {
              ${actorPartials}
            }
          }
        }
      }
    `

    const sets = []
    const collapsedIds = []
    collapsedChangesets.forEach(changeset => {
      if (isEmpty(changeset.actions)) return
      const action = changeset.actions[0]
      const change = action.change
      if (!change) return
      collapsedIds.push(changeset.id)
      sets.push({
        leaderId: changeset.id,
        pieceChangesetIds: change.subchangeset_ids,
      })
    })

    dispatch({
      type: types.BULK_FETCH_CHANGESET_TICKET_ACTIONS_REQUEST,
      data: { changesetIds: collapsedIds },
    })

    const chunks = chunk(sets, 3)

    const run = () => {
      if (chunks.length === 0) return // we're done
      const singleChunk = chunks.pop()
      let changesetIds = []

      singleChunk.forEach(piece => {
        changesetIds = changesetIds.concat(piece.pieceChangesetIds)
      })

      const variables = {
        ticketId,
        changesetIds,
      }

      // eslint-disable-next-line consistent-return
      return graphql(token, query, variables).then(res => {
        const data = res.json.data
        const records = data.actions.records
        const lookup = {}
        records.forEach(action => {
          const actionChangesetId = action.changeset
          lookup[actionChangesetId] = lookup[actionChangesetId] || []
          lookup[actionChangesetId].push(action)
        })
        const payloads = []
        singleChunk.forEach(piece => {
          const { leaderId, pieceChangesetIds } = piece
          let actions = []
          pieceChangesetIds.forEach(
            id => (actions = actions.concat(lookup[id]))
          )
          payloads.push({
            ticketId,
            sourceChangesetId: leaderId,
            actions,
          })
        })
        dispatch({
          type: types.BULK_FETCH_CHANGESET_TICKET_ACTIONS_SUCCESS,
          data: { ticketId, payloads },
        })

        setTimeout(run, 1000) // space out
      })
    }
    run()
  }
}

export function doFetchFirstFolderTicket() {
  return (dispatch, getState) => {
    const state = getState()
    const tickets = currentFolderTicketsSelector(state)
    if (!tickets || getLength(tickets) <= 0) return false
    const ticket = tickets[0]
    const ticketId = ticket.id
    if (ticketId === 'new') return false
    if (!ticketId) return false

    return dispatch(doFetchTicket(ticketId))
  }
}

export function doUpdateCurrentMailbox(currentMailbox) {
  return {
    type: types.UPDATE_CURRENT_MAILBOX,
    data: {
      currentMailbox,
    },
  }
}

export function doAddPageToCurrentFolder(folder, mailboxId, pageNo) {
  return {
    type: types.ADD_PAGE_TO_CURRENT_FOLDER,
    data: {
      page: pageNo,
      folder,
      mailboxId,
    },
  }
}

export function doUpdateCurrentTicket(currentTicket) {
  return {
    type: types.FETCH_TICKET_SUCCESS,
    data: {
      currentTicket,
    },
  }
}

export function doUpdateFirstMailbox(firstMailbox) {
  return {
    type: types.UPDATE_FIRST_MAILBOX,
    data: {
      firstMailbox,
    },
  }
}

export function doAssignTicketToGroupAndAgent(
  ticketId,
  groupId,
  agentId,
  options
) {
  return (dispatch, getState) => {
    const state = getState()
    const mailboxId = selectCurrentMailboxId(state)
    const currentUser = selectCurrentUser(state)
    dispatch({
      type: types.ASSIGN_TICKET_TO_AGENT,
      data: {
        ticketId,
        agentId,
        mailboxId,
        groupId,
        currentUserId: currentUser.id,
      },
    })
    const assignee = agentId ? { id: agentId } : null
    const data = {
      assigned_group_id: groupId,
      assignee,
    }
    const optimisticData = assignmentPayload(
      state,
      ticketId,
      groupId,
      agentId,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state)
    )
    const newOpts = { ...options, optimisticData }

    dispatch(doShowSnackbar('Conversation reassigned'))
    if (groupId) {
      dispatch(doAssignGroupToDraft(ticketId, groupId))
    } else {
      dispatch(doAssignAgentToDraft(ticketId, agentId))
    }
    return dispatch(doSendChangeset(ticketId, data, newOpts))
  }
}

export function doAssignToSelf(ticketId, options) {
  return (dispatch, getState) => {
    const state = getState()
    const currentUser = selectCurrentUser(state)
    const byId = selectTicketsById(state)
    const existingGroup = selectGroupsById(state)[
      byId[ticketId].assigned_group_id
    ]
    const groupId =
      existingGroup && isAgentInGroup(existingGroup, currentUser.id)
        ? existingGroup.id
        : null
    return dispatch(
      doAssignTicketToGroupAndAgent(ticketId, groupId, currentUser.id, options)
    )
  }
}

export function doToggleStarred(ticketId, newPriority) {
  return (dispatch, getState) => {
    const state = getState()
    const ticket = selectTicketsById(state)[ticketId]

    const newPriorityOrInverted =
      newPriority || (isStarred(ticket) ? 'low' : 'urgent')

    const changesetId = uuidV4()
    const optimisticData = starPayload(
      state,
      ticketId,
      newPriorityOrInverted,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state),
      changesetId
    )

    return dispatch(
      doSendChangeset(
        ticketId,
        { priority: newPriorityOrInverted },
        { optimisticData }
      )
    ).catch(err => {
      const starred = newPriorityOrInverted === 'urgent'
      const type = starred ? 'star' : 'unstar'
      // eslint-disable-next-line no-console
      console.error(`${type} ticket failed`, { err, ticketId, newPriority })
      dispatch(doShowSnackbar(`Oops, we couldn't ${type} your conversation.`))
    })
  }
}

export function doStarTicket(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const isTicketStarred = selectCurrentTicketIsStarred(state)
    if (isTicketStarred) return false
    return dispatch(doToggleStarred(ticketId, 'urgent'))
  }
}

export function doUnstarTicket(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const isTicketStarred = selectCurrentTicketIsStarred(state)
    if (!isTicketStarred) return false
    return dispatch(doToggleStarred(ticketId, 'low'))
  }
}

function doRawUpdateTickets(tickets, options) {
  return {
    type: types.UPDATE_TICKETS,
    data: {
      tickets,
      options,
    },
  }
}

// NOTE (jscheel): If you try to update a ticket with a new customer reference
// and that customer does not exist in the store, this action creator will
// attempt to fetch the missing customers so that the reference does not point
// to an undefined object.
export function doUpdateTickets(tickets, options = {}) {
  return (dispatch, getState) => {
    const state = getState()
    const ids = tickets
      .map(ticket => {
        const customer = ticket.customer
        if (customer && !selectCustomersById(state)[customer.id]) {
          return customer.id
        }
        return undefined
      })
      .filter(id => id !== undefined)
    if (ids.length > 0) {
      dispatch(doRawUpdateTickets(tickets, options))
    } else {
      dispatch(doRawUpdateTickets(tickets, options))
    }
  }
}

export function doSearchUsers(term) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const isForwarding = selectDraftIsForwarded(state)

    dispatch({ type: types.SEARCH_USER_REQUEST })

    const success = {
      type: types.SEARCH_USER_SUCCESS,
      data: { term, isForwarding },
    }
    const complete = { type: types.SEARCH_USER_COMPLETE }

    const cacheKey = (isForwarding ? 'forward:' : '') + term
    const previousSearchResults = selectPreviousUserSearchResults(state)[
      cacheKey
    ]
    if (previousSearchResults) {
      success.data.users = previousSearchResults
      dispatch(batchActions([success, complete]))
      return Promise.resolve()
    }

    const exists = ['email']
    const defaultRoles = ['admin', 'owner', 'agent']
    const role = defaultRoles.concat(isForwarding ? 'collaborator' : 'enduser')

    const scopeByMailbox = selectAccountPrefersScopedContactsSearch(state)
    let mailboxIds = null
    if (scopeByMailbox) {
      const draftMailbox = selectDraftMailbox(state)
      if (draftMailbox) {
        mailboxIds = [draftMailbox.id]
      }
    }

    return grooveAPI
      .get(token, 'v1/contacts.json', {
        term,
        exists,
        role,
        mailbox_ids: mailboxIds,
      })
      .then(res => {
        success.data.users = res.json.contacts
        dispatch(success)
      })
      .catch(err => {
        debug(err)
        dispatch({ type: types.SEARCH_USER_FAIL })
      })
      .then(() => {
        dispatch(complete)
      })
  }
}

export function doClearUserSuggestion() {
  return {
    type: types.CLEAR_USER_SUGGESTION,
  }
}

export function doToggleTicketsSorting() {
  return {
    type: types.TOGGLE_TICKETS_SORTING,
  }
}

export function doFetchRawEmail() {
  return (dispatch, getState) => {
    dispatch(doMarkFetchingStatus('fetchRawEmail', true))
    const state = getState()
    const commentId = selectCurrentRawEmailId(state)
    if (!commentId) return
    const query = `
      query RawEmailQuery {
        rawEmail(id: "${commentId}") {
          headers
          plain
          html
          ticket_id
        }
      }`

    const token = oauthTokenSelector(state)

    graphql(token, query)
      .then(res => {
        const data = res.json.data.rawEmail
        data.commentId = commentId
        data.retrieved = true
        dispatch(doUpdateRawEmail(data))
      })
      .catch(err => {
        debug(err)
        dispatch(doUpdateRawEmail({ commentId, retrieved: false }))
      })
      .then(() => {
        dispatch(doMarkFetchingStatus('fetchRawEmail', false))
      })
  }
}

export function doUpdateRawEmail(rawEmail) {
  return {
    type: types.UPDATE_RAW_EMAIL,
    data: {
      rawEmail,
    },
  }
}

export function doToggleShowCcBcc() {
  return {
    type: types.TOGGLE_SHOW_CC_BCC,
  }
}

export function doSetTicketAssignmentFilter(term) {
  return {
    type: types.SET_TICKET_ASSIGNMENT_FILTER,
    data: {
      term,
    },
  }
}

export function doUpdateTicketDirtyStatus(ticketId, status) {
  return {
    type: types.UPDATE_TICKET_DIRTY_STATUS,
    data: {
      ticketId,
      dirty: status,
    },
  }
}

export function doCurrentMailboxChanged(ticketId, mailboxId) {
  return (dispatch, getState) => {
    const state = getState()
    const optimisticData = mailboxPayload(
      state,
      ticketId,
      mailboxId,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state)
    )
    const actions = []
    actions.push(doSendChangeset(ticketId, { mailboxId }, { optimisticData }))
    actions.push(doHideModal(modals.MAILBOX_CHANGE))
    dispatch(batchActions(actions))
  }
}

export function doBulkChangeMailbox(ticketIds, mailboxId) {
  return (dispatch, getState) => {
    const state = getState()
    const currentTicketId = selectCurrentTicketId(state)
    const autoAdvanceToNextTicketInList = selectAutoAdvanceToNextTicketInList(
      state
    )
    const mailbox =
      selectInaccessibleMailbox(state, mailboxId) ||
      selectMailbox(state, mailboxId)
    const { name } = mailbox || {}

    const snackbar = {
      message: `${pluralize(
        ticketIds.length,
        app.t('Ticket')
      )} moved to "${name}"`,
    }

    if (ticketIds.length === 1 && autoAdvanceToNextTicketInList) {
      snackbar.link = `/tickets/${ticketIds[0]}`
      snackbar.linkText = `Go back to ${app.t('Ticket')}`
    }

    const optimisticData = bulkMailboxPayload(
      state,
      ticketIds,
      mailboxId,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state)
    )
    const inputs = ticketIds.map(id => {
      return { ticketId: id, mailboxId, includeTicketInChangesetResponse: true }
    })
    const actions = []
    actions.push(
      doSendBulkChangeset(inputs, {
        optimisticData,
        snackbar,
      })
    )
    actions.push(doHideModal(modals.MAILBOX_CHANGE))
    if (ticketIds.length === 1 && ticketIds[0] === currentTicketId) {
      actions.push(doUpdateDraftMailbox(mailboxId))
    }
    dispatch(batchActions(actions))
  }
}

export function doChangeCurrentTicketMailbox(mailboxId) {
  return (dispatch, getState) => {
    const state = getState()
    const ticketId = selectCurrentTicketId(state)

    dispatch(doBulkChangeMailbox([ticketId], mailboxId))
  }
}

export function doChangeSelectedTicketsMailbox(mailboxId) {
  return (dispatch, getState) => {
    const state = getState()
    const ticketIds = selectSelectedTicketIds(state)

    dispatch(doBulkChangeMailbox(ticketIds, mailboxId))
  }
}

export function doFetchOptimisticMergeTickets(ticketIds) {
  return (dispatch, getState) => {
    if (!ticketIds) return Promise.reject(new Error('no ticket ids supplied'))

    dispatch({
      type: types.FETCH_OPTIMISTIC_MERGE_TICKETS_REQUEST,
      data: {
        ticketIds,
      },
    })

    const state = getState()
    const queries = ticketIds.map(id => {
      return `
        ticket${id}: ticket(id: ${id}) {
          id
          message_count
          state
          actions {
            records {
              created_at
              changeset
              preview
              change_type
              change {
                ${actionPartials}
              }
              actor {
                ${actorPartials}
              }
            }
          }
        }
      `
    })
    const query = `
      query TicketQuery {
        ${queries.join('\n')}
      }
    `
    const token = oauthTokenSelector(state)

    return graphql(token, query).then(res => {
      const data = res.json.data
      const tickets = {}

      Object.keys(data).forEach(key => {
        tickets[key.replace('ticket', '')] = data[key]
      })

      return dispatch({
        type: types.FETCH_OPTIMISTIC_MERGE_TICKETS_SUCCESS,
        data: {
          ticketIds,
          tickets,
        },
      })
    })
  }
}

export function doBulkAssign(ticketIds, groupId, agentId) {
  return (dispatch, getState) => {
    const mailboxId = selectCurrentMailboxId(getState())
    dispatch({
      type: types.ASSIGN_TICKETS_TO_AGENT,
      data: { ticketIds, mailboxId, groupId, agentId },
    })

    const state = getState()
    const optimisticData = bulkAssignmentPayload(
      state,
      ticketIds,
      groupId,
      agentId,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state)
    )

    const agent = agentId ? selectAgentsById(state)[agentId] : null

    const inputs = ticketIds.map(id => {
      return {
        ticketId: id,
        assigned_group_id: groupId,
        assignee: agent,
      }
    })

    return dispatch(
      doSendBulkChangeset(inputs, {
        optimisticData,
        snackbar: { message: `${ticketIds.length} reassigned` },
      })
    ).catch(() => {
      dispatch(
        doShowSnackbar("Oops, we couldn't reassign those conversations.")
      )
    })
  }
}

const insertAgentMention = agent => {
  if (!agent) return false

  return setTimeout(() => {
    editor.fire('mention-insert', {
      mention: `@${agent.username}`,
    })

    // eslint-disable-next-line no-undef
    tinymce.activeEditor.fire('change')
  }, 50)
}

export function doMentionAgent(agentId) {
  return (dispatch, getState) => {
    const state = getState()
    const agent = selectAgentsById(state)[agentId]
    if (!agent) return Promise.reject('cannot find agent')

    const agentMention = selectAgentMention(state)
    // NOTE (jscheel): If we are mentioning an agent from a partial match that
    // is typed in the editor, we use that partial match. Otherwise, we try to
    // insert the mention normally.
    if (agentMention) {
      if (editor.available()) {
        editor.fire('mention-select', {
          mention: `@${agent.username}`,
        })

        // eslint-disable-next-line no-undef
        tinymce.activeEditor.fire('change')
      }
      return dispatch(doUpdateAgentMention(null))
    }

    const ticketId = selectCurrentTicketId(state)
    const isAddingNote = selectIsAddingNote(state)

    if (!isAddingNote) dispatch(redirect(doOpenNotePage(ticketId)))

    runOnNextTick(() => {
      insertAgentMention(agent)
    })

    return Promise.resolve(agent)
  }
}

export function doAssignCurrentOrSelectedToCurrentUser() {
  return (dispatch, getState) => {
    const state = getState()
    const tickets = selectCurrentOrSelectedTickets(state)
    const currentUser = selectCurrentUser(state)
    const areAllAssignedToCurrentUser = all(ticket => {
      return ticket.assignee && ticket.assignee.id === currentUser.id
    }, tickets)
    const assignTo = areAllAssignedToCurrentUser ? null : currentUser.id
    const ticketIds = tickets.map(ticket => ticket.id)
    dispatch(doBulkAssign(ticketIds, null, assignTo))
  }
}

export function doFollowTicket(
  ticketId,
  follow,
  { suppressSnackbar = false } = {}
) {
  return (dispatch, getState) => {
    const state = getState()
    const ticket = selectTicketsById(state)[ticketId]
    const currentUserId = selectCurrentUser(state).id
    const optimisticData = followPayload(
      state,
      ticketId,
      selectCurrentFolderId(state),
      selectLatestTicketSearchQueryObject(state),
      follow,
      ticket.follower_ids,
      currentUserId
    )
    const actions = [
      doSendChangeset(
        ticketId,
        { follows: follow },
        { optimisticData, skipTicketTouch: true }
      ),
    ]
    if (!suppressSnackbar) {
      actions.push(
        doShowSnackbar(`Conversation ${follow ? 'followed' : 'unfollowed'}.`)
      )
    }
    return dispatch(batchActions(actions))
  }
}

export function doFollowCurrentTicket(follow = true) {
  return (dispatch, getState) => {
    const state = getState()
    const currentTicketId = selectCurrentTicketId(state)
    return dispatch(doFollowTicket(currentTicketId, follow))
  }
}
export function doUnfollowCurrentTicket() {
  return doFollowCurrentTicket(false)
}
